import { useCallback } from "react";

import nookies from "nookies";

import { AmplitudeEvents } from "../constants/amplitude";

import { useAmplitude } from "./amplitude/useAmplitude";

import { pushGtmFormInputChanged } from "@/common/helpers/gtm";
interface CountCookie {
  count: number;
}

// Name of the cookie
const COOKIE_NAME = "_z_ficc";

// Default max age for the cookie in days
const DEFAULT_MAX_AGE_DAYS = 7;

export const useFormInputChanged = () => {
  const { logAmplitudeEvent } = useAmplitude();

  const getUpdatedCookieData = useCallback((): CountCookie => {
    const defaultCookieData: CountCookie = {
      count: 1,
    };

    // For client-side usage, pass null as context
    const cookies = nookies.get(null);

    if (cookies[COOKIE_NAME]) {
      try {
        const cookie: CountCookie = JSON.parse(cookies[COOKIE_NAME]);
        if (cookie && cookie.count) {
          // If the cookie exists, increment the count
          return { count: cookie.count + 1 };
        } else {
          // Otherwise, return default cookie.
          return defaultCookieData;
        }
      } catch {
        // If parsing fails, we'll ignore the old cookie
        return defaultCookieData;
      }
    }

    return defaultCookieData;
  }, []);

  const updateCookieAndPushGtmEvent = useCallback(() => {
    const updatedCookieData: CountCookie = getUpdatedCookieData();

    // Save the cookie with the updated count
    nookies.set(null, COOKIE_NAME, JSON.stringify(updatedCookieData), {
      maxAge: DEFAULT_MAX_AGE_DAYS * 24 * 60 * 60,
      path: "/",
    });

    // Push the GTM event with the updated count
    pushGtmFormInputChanged(updatedCookieData.count);
  }, [getUpdatedCookieData]);

  const formInputChanged = useCallback(
    (payload: Record<string, string | number | boolean>) => {
      logAmplitudeEvent(AmplitudeEvents.FormInputChanged, payload);
      updateCookieAndPushGtmEvent();
    },
    [logAmplitudeEvent, updateCookieAndPushGtmEvent]
  );

  const formInputChangedCallback = useCallback(
    (payload: Record<string, string | number | boolean>) => () => formInputChanged(payload),
    [formInputChanged]
  );

  return { formInputChanged, formInputChangedCallback };
};
